/** Phone number types returned by the API */
export type PhoneNumberType = 'CALLCENTER' | 'HIDDEN' | 'VOICEBOX' | 'SERVICE';

/** Inventory selector for the bookable country/city lookups (`phoneNumberType` query param). */
export type NumberLookupType = 'CALLCENTER' | 'FAX';

/** Tag assigned to a phone number (API returns `{ id, name }` objects, not plain strings). */
export interface NumberTag {
  id: number;
  name: string;
}

/** Number block a phone number belongs to (RUFNR_BLOCK). All fields come straight from the API. */
export interface NumberBlock {
  id: number;
  vorwahl: string;
  prefix: string;
  suffixStart: string;
  amount: number;
  /** Internal short prefix; short notation = shortForm + suffix (legacy `RUB_SHORT || PHN_SUFFIX`) */
  shortForm: string;
  countryPrefix: string;
  cityPrefix: string;
  freeOfCharge: boolean;
  carrier: string;
  callcenterId: number;
}

export interface PhoneNumber {
  id: number;
  number: string;
  type: PhoneNumberType;
  /** Tags assigned to this number */
  tags: NumberTag[];
  /** Number block info (if part of a block) */
  numberBlock: NumberBlock | null;
  /** Block-local suffix; combined with numberBlock.shortForm forms the internal short notation */
  suffix: string;
  /** Voxbone provider type (e.g. carrier integration), if any */
  providerType: string | null;
  /** Voxbone provider id, if any */
  providerId: string | null;
  /** Links to a stored regulation address (VOXBONE_ADDRESS) when the number is regulated */
  regulationAddressId: number | null;
  /** Voxbone/regulation status */
  currentRegulationStatus: string | null;
  /** Human-readable regulation message */
  regulationMessage: string | null;
  /** Reasons for rejection (if rejected) */
  rejectionReasons: string[] | null;
  /** Carrier the number is provisioned on, e.g. OPENNUMBERS; OUTBOX is the SIP carrier. */
  carrier?: string | null;
}

/**
 * Internal short notation of a number = numberBlock.shortForm + suffix
 * (the legacy DB expression `RUB_SHORT || PHN_SUFFIX`). Null-safe: falls back to suffix only.
 */
export function shortNotation(n: Pick<PhoneNumber, 'numberBlock' | 'suffix'>): string {
  return `${n.numberBlock?.shortForm ?? ''}${n.suffix ?? ''}`;
}

// --- Stored regulation address (lazy, failed/rejected numbers only) ---

/**
 * Response of GET /phoneservices/{psId}/numbers/{number}/regulationAddresses
 * (PS-API `PreFilledRegulationData`). Only resolvable for REJECTED / NO_REGULATION_ADDRESS_FOUND /
 * FAILED states — the endpoint is a prefill for re-submitting failed regulations, not a generic getter.
 * Only the subset we display is typed; binary proof files are intentionally omitted.
 */
export interface PreFilledRegulationData {
  /** Field requirements for the number's city/country (which fields are mandatory). */
  regulationInformation?: RegulationInformation | null;
  filledData?: {
    fields?: {
      regulationInformation?: {
        companyName?: string | null;
        companyVat?: string | null;
        streetName?: string | null;
        buildingNumber?: string | null;
        buildingLetter?: string | null;
        zipCode?: string | null;
        cityName?: string | null;
        isoCountryCode?: string | null;
        province?: string | null;
        state?: string | null;
        identityDocumentProof?: {
          salutation?: string | null;
          firstName?: string | null;
          lastName?: string | null;
          nationality?: string | null;
          identityDocumentType?: string | null;
          identityDocumentNumber?: string | null;
          dateOfBirth?: unknown;
          placeOfBirth?: string | null;
          taxCode?: string | null;
          issuingAuthority?: string | null;
          issuingDate?: unknown;
        } | null;
      } | null;
    } | null;
  } | null;
}

/** Flat, display-ready view of the stored regulation address (PII/KYC). */
export interface RegulationAddressDetails {
  company: string | null;
  companyVat: string | null;
  street: string | null;
  buildingNumber: string | null;
  buildingLetter: string | null;
  zipCode: string | null;
  city: string | null;
  province: string | null;
  state: string | null;
  countryCode: string | null;
  salutation: string | null;
  firstName: string | null;
  lastName: string | null;
  nationality: string | null;
  idType: string | null;
  idNumber: string | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  taxCode: string | null;
  issuingAuthority: string | null;
  issuingDate: string | null;
}

/** A LocalDate may serialize as "yyyy-MM-dd" or as a [y,m,d] array depending on Jackson config. */
function normalizeDate(value: unknown): string | null {
  if (value == null) return null;
  return Array.isArray(value) ? value.join('-') : String(value);
}

/** Map the prefill response to the flat display view. Returns null when no address data is present. */
export function mapRegulationAddress(res: PreFilledRegulationData | null): RegulationAddressDetails | null {
  const info = res?.filledData?.fields?.regulationInformation;
  if (!info) return null;
  const id = info.identityDocumentProof ?? {};
  return {
    company: info.companyName ?? null,
    companyVat: info.companyVat ?? null,
    street: info.streetName ?? null,
    buildingNumber: info.buildingNumber ?? null,
    buildingLetter: info.buildingLetter ?? null,
    zipCode: info.zipCode ?? null,
    city: info.cityName ?? null,
    province: info.province ?? null,
    state: info.state ?? null,
    countryCode: info.isoCountryCode ?? null,
    salutation: id.salutation ?? null,
    firstName: id.firstName ?? null,
    lastName: id.lastName ?? null,
    nationality: id.nationality ?? null,
    idType: id.identityDocumentType ?? null,
    idNumber: id.identityDocumentNumber ?? null,
    // dateOfBirth/issuingDate may serialize as "yyyy-MM-dd" or as a [y,m,d] array depending on Jackson config
    dateOfBirth: normalizeDate(id.dateOfBirth),
    placeOfBirth: id.placeOfBirth ?? null,
    taxCode: id.taxCode ?? null,
    issuingAuthority: id.issuingAuthority ?? null,
    issuingDate: normalizeDate(id.issuingDate),
  };
}

// --- Fix regulation (EB-3890) ---

/** Field requirements + prefilled values for the fix form, derived from the prefill response. */
export interface RegulationPrefill {
  requirements: RegulationInformation | null;
  values: RegulationAddressDetails | null;
}

/** Split the prefill response into requirements (mandatory fields) + current values. */
export function getRegulationPrefill(res: PreFilledRegulationData | null): RegulationPrefill {
  return {
    requirements: res?.regulationInformation ?? null,
    values: mapRegulationAddress(res),
  };
}

/** Validated payload produced by the shared regulation form, ready to submit to the fix PUT. */
export interface RegulationFormValue {
  /** JSON object matching the PS-API `RegulationInformation` (address + nested identityDocumentProof). */
  regulationInformation: Record<string, unknown>;
  numberType: PhoneNumberType;
  addressProofFile?: File | null;
  identityProofFile?: File | null;
}

/** Result of the fix PUT — the PS-API returns a VoxboneStatus value. */
export interface VoxboneStatusResult {
  status: string;
}

export interface WaitingQueueStatus {
  status: 'ACTIVE' | 'INACTIVE' | 'REPAIR';
}

/** Voicebox type constants */
export const AM_TYPES = [
  { value: 'AB-nicht erreichbar', label: 'AB-nicht erreichbar' },
  { value: 'AB-no-recording', label: 'AB-no-recording' },
  { value: 'AB-ringing', label: 'AB-ringing' },
] as const;

export type AmType = typeof AM_TYPES[number]['value'];

/** Linked number reference from AM info */
export interface LinkedCallCenterNumber {
  number: string;
  type: PhoneNumberType;
  id: string;
}

/** Full answering machine info from GET /phoneservices/{psId}/answeringmachine */
export interface AnsweringMachineInfo {
  language: string;
  password: string;
  type: string | null;
  /** Public vs dummy voicebox number. Absent in current v3 response → treated as public (matches old CMT). */
  withDummyNumber?: boolean;
  voiceBoxNumber: LinkedCallCenterNumber | null;
  linkedCallCenterNumbers: LinkedCallCenterNumber[];
}

/** Regulation address requirements (from city API) */
export interface RegulationAddressRequirements {
  companyName?: boolean;
  companyVat?: boolean;
  streetName?: boolean;
  buildingNumber?: boolean;
  buildingLetter?: boolean;
  zipCode?: boolean;
  city?: boolean;
  province?: boolean;
  state?: boolean;
  countryCodeA3?: boolean;
  proofDocument?: boolean;
}

/** Regulation identity requirements (from city API) */
export interface RegulationIdentityRequirements {
  salutation?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  dateOfBirth?: boolean;
  placeOfBirth?: boolean;
  taxCode?: boolean;
  identityDocumentNumber?: boolean;
  identityDocumentType?: boolean;
  issuingAuthority?: boolean;
  issuingDate?: boolean;
  nationality?: boolean;
  proofDocument?: boolean;
}

/** Fixed values pre-filled by the API (read-only in the form) */
export interface RegulationFixedValues {
  city?: string;
  countryCodeA3?: string;
  [key: string]: string | undefined;
}

/** Full regulation information from city API */
export interface RegulationInformation {
  addressRequirements?: RegulationAddressRequirements;
  identityRequirements?: RegulationIdentityRequirements;
  fixedValues?: RegulationFixedValues;
}

export interface Country {
  isoCountryCode: string;
  name: string;
  prefix: string;
}

export interface City {
  name: string;
  prefix: string;
  regulationInformation: RegulationInformation | null;
}

export interface Zipcode {
  zipCode: string;
  cityName?: string;
}

/** Booking payload for POST /phoneservices/{psId}/numbers */
/**
 * Multipart booking input for POST /phoneservices/{psId}/numbers.
 * `phoneNumberData` is sent as a JSON blob part; the optional proof files are sent as
 * `proofOfAddressFile` / `proofOfIdFile` (same file-part names as the regulation-fix PUT —
 * see CcNumbersService.fixRegulationAddress). Structurally matches the dialog's
 * BookNumberDialogResult.
 */
export interface BookNumberPayload {
  phoneNumberData: {
    desiredCountryPrefix: string;
    desiredCityPrefix: string;
    desiredCityName: string;
    type: PhoneNumberType;
    employeeId: string;
    /** Present only for regulated cities; dynamic fields per country/city requirements. */
    regulationInformation?: unknown;
  };
  addressProofFile?: File;
  identityProofFile?: File;
}

/** AM booking payload for POST /phoneservices/{psId}/numbers/{number}/answeringmachine */
export interface AmBookPayload {
  email?: string;
  language?: string;
  type?: string;
  withDummyNumber?: boolean;
  /** Target CC number (mode 'inactive') — chosen in the dialog, used for the URL path (stripped from the body). */
  number?: string;
}

/** Voxbone statuses that indicate a problem */
export const VOXBONE_ERROR_STATUSES = [
  'NO_REGULATION_ADDRESS_FOUND',
  'REJECTED',
  'FAILED',
];

export const VOXBONE_PENDING_STATUSES = [
  'VERIFICATION_REQUESTED',
];

/** Statuses that mean "all good" or no regulation needed */
export const VOXBONE_OK_STATUSES = [
  null,
  'VERIFICATION_NOT_REQUIRED',
  'NUMBERS_LINKED_AND_UNBLOCKED',
];

// --- Address check (PS-6489 / PS-6488) ---------------------------------------------------------
// Contract copied from phoneservice-api branch PS-6488_geocode-address-check:
// LocationController.checkAddress, AddressCheckRequest, AddressCheckResponse, AddressCheckVerdict.

/** Verdict of the geocode address check. Mirrors AddressCheckVerdict (backend enum). */
export type AddressCheckVerdict =
  | 'MATCH'
  | 'ADDRESS_NORMALIZED'
  | 'AREACODE_MISMATCH'
  | 'NOT_FOUND'
  | 'PROVIDER_UNAVAILABLE'
  | 'NOT_APPLICABLE';

/** Request body of POST /countries/{iso}/cities/{cityPrefix}/addresschecks.
 *  street/zipCode/city are @NotBlank server-side; buildingNumber is optional in the DTO but a
 *  blank one is answered with NOT_FOUND (AddressCheckService:45). */
export interface AddressCheckRequest {
  street: string;
  buildingNumber: string;
  zipCode: string;
  city: string;
}

/** Cleaned-up address split into fields — present whenever the address was located. */
export interface AddressCheckComponents {
  street?: string;
  buildingNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

/** Response of the address check (PS-6493 contract, Confluence "Address & Area-Code Check").
 *  Everything marked "when found" is absent unless the address was located, so nothing below
 *  `mismatchedFields` may be rendered unconditionally.
 *
 *  NOTE: `connectionArea`, `municipalityKey` and `emergencyServices` are declared by the backend
 *  but never filled — they exist only for shape compatibility with the retired OpenNumbers check.
 *  They are deliberately NOT modelled here. */
export interface AddressCheckResponse {
  verdict: AddressCheckVerdict;
  requestedCityPrefix: string;
  addressChanged: boolean;
  /** Whether the ONKZ cross-check actually ran. False for national numbers (prefix 32/3222) and
   *  whenever the address was not found — a positive verdict with false means NOT area-verified. */
  areaCodeChecked: boolean;
  normalizedAddress?: string;
  /** Official ONKZ of the found address, WITH leading zero ("030"). Absent for national numbers. */
  areaCode?: string;
  latitude?: number;
  longitude?: number;
  normalizedComponents?: AddressCheckComponents;
  /** Fields that differ from the request: street | buildingNumber | zipCode | city | areaCode. */
  mismatchedFields?: string[];
}

/** Verdicts that must prevent booking. PROVIDER_UNAVAILABLE is deliberately NOT in this list:
 *  fail-open on a provider outage, fail-closed only on a definite negative (PS-6483, 2026-08-20). */
export const ADDRESS_CHECK_BLOCKING_VERDICTS: readonly AddressCheckVerdict[] = [
  'AREACODE_MISMATCH',
  'NOT_FOUND',
];
